(function () {
  'use strict';

  /**
   * BtsRemovedItemsModal
   * A self-contained modal for notifying users that unavailable cart items
   * were automatically removed. Completely independent of the theme's popup system.
   */
  window.BtsRemovedItemsModal = {
    _overlay: null,
    _modal: null,
    _onKeyDown: null,

    // -------------------------------------------------------
    // Public API
    // -------------------------------------------------------

    /**
     * Show the modal with a list of removed items.
     * @param {Array} items - Array of removed item objects:
     *   { sku, title, url, image, quantity, kitOptions }
     */
    show(items) {
      this._build(items);
      // Force reflow before adding visible class so CSS transition fires
      this._overlay.getBoundingClientRect();
      this._overlay.classList.add('bts-modal--visible');
      document.body.style.overflow = 'hidden';

      // ESC key to close
      this._onKeyDown = (e) => {
        if (e.key === 'Escape') this.hide();
      };
      document.addEventListener('keydown', this._onKeyDown);
    },

    hide() {
      if (!this._overlay) return;
      this._overlay.classList.remove('bts-modal--visible');
      document.body.style.overflow = '';

      if (this._onKeyDown) {
        document.removeEventListener('keydown', this._onKeyDown);
        this._onKeyDown = null;
      }

      // Remove from DOM after transition
      const overlay = this._overlay;
      setTimeout(() => {
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }, 250);

      this._overlay = null;
      this._modal = null;
    },

    // -------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------

    _escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    _buildItemsHtml(items) {
      return items.map((item) => {
        const imgHtml = item.image
          ? `<a href="${this._escapeHtml(item.url || '#')}" class="bts-modal__img-link" tabindex="-1" aria-hidden="true">
               <img src="${this._escapeHtml(item.image)}" alt="${this._escapeHtml(item.title)}" class="bts-modal__img" loading="lazy">
             </a>`
          : `<div class="bts-modal__img-placeholder" aria-hidden="true"></div>`;

        let kitHtml = '';
        if (item.kitOptions && item.kitOptions.length > 0) {
          const groupsHtml = item.kitOptions
            .filter((g) => g.items && g.items.length > 0)
            .map((group) => {
              const itemsHtml = group.items
                .map(
                  (ki) =>
                    `<li class="bts-modal__kit-item">${this._escapeHtml(ki.name)} &times; ${ki.qty}</li>`
                )
                .join('');
              return `<div class="bts-modal__kit-group">
                <span class="bts-modal__kit-group-name">${this._escapeHtml(group.name)}:</span>
                <ul class="bts-modal__kit-items">${itemsHtml}</ul>
              </div>`;
            })
            .join('');

          if (groupsHtml) {
            kitHtml = `<div class="bts-modal__kit">${groupsHtml}</div>`;
          }
        }

        const qtyLabel =
          (window.kitSelector && window.kitSelector.autoRemovedQtyLabel) || 'Qty';

        return `<li class="bts-modal__item">
          ${imgHtml}
          <div class="bts-modal__details">
            <div class="bts-modal__name">
              <a href="${this._escapeHtml(item.url || '#')}">${this._escapeHtml(item.title)}</a>
            </div>
            <div class="bts-modal__qty">${this._escapeHtml(qtyLabel)}: ${item.quantity}</div>
            ${kitHtml}
          </div>
        </li>`;
      }).join('');
    },

    _build(items) {
      // Remove any existing instance
      const existing = document.getElementById('bts-removed-modal-overlay');
      if (existing) existing.parentNode.removeChild(existing);

      const title =
        (window.kitSelector && window.kitSelector.autoRemovedTitle) ||
        'Products removed from your cart';
      const message =
        (window.kitSelector && window.kitSelector.autoRemovedMessage) ||
        'The following unavailable products were automatically removed from your cart.';
      const confirmLabel =
        (window.kitSelector && window.kitSelector.autoRemovedConfirm) ||
        'OK, got it';

      const overlay = document.createElement('div');
      overlay.id = 'bts-removed-modal-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'bts-modal-title');

      overlay.innerHTML = `
        <div id="bts-removed-modal">
          <div class="bts-modal__header">
            <h2 class="bts-modal__title" id="bts-modal-title">${this._escapeHtml(title)}</h2>
            <button class="bts-modal__close" aria-label="Close" id="bts-modal-close-btn">&#x2715;</button>
          </div>
          <p class="bts-modal__subtitle">${this._escapeHtml(message)}</p>
          <ul class="bts-modal__list" aria-label="Removed products">
            ${this._buildItemsHtml(items)}
          </ul>
          <div class="bts-modal__footer">
            <button class="bts-modal__confirm" id="bts-modal-confirm-btn">${this._escapeHtml(confirmLabel)}</button>
          </div>
        </div>`;

      document.body.appendChild(overlay);
      this._overlay = overlay;
      this._modal = overlay.querySelector('#bts-removed-modal');

      // Attach close handlers
      overlay.querySelector('#bts-modal-close-btn').addEventListener('click', () => this.hide());
      overlay.querySelector('#bts-modal-confirm-btn').addEventListener('click', () => this.hide());

      // Click on overlay backdrop (outside modal card) closes
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) this.hide();
      });
    },
  };
})();
